// CalendarEventForm — форма создания/редактирования события календаря.
// Поля: title + description + location + startAt + endAt + type +
// обязательный «Департамент» (B1: POST /api/calendar/events требует @NotNull departmentId;
// U-20, sled-V9 W6: DepartmentAutocomplete — поисковый селект вместо 1258 MenuItem).
// U-30: zod-схема — фабрика с t: cross-field refine start < end (не уходить на сервер с endAt <= startAt).
import { Controller, useForm, type SubmitHandler } from 'react-hook-form';
import { useMemo } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Stack, TextField, MenuItem, Button, Box, FormControlLabel, Switch } from '@mui/material';
import { startOfDay, endOfDay } from 'date-fns';
import { DateTimePickerField } from '@/components/forms';
import { DepartmentAutocomplete } from '@/components/DepartmentAutocomplete';
import type { CalendarType } from '@/api/calendar';
import { t } from '@/i18n';
import { formatEventType } from '@/lib/format';

/** Подпись translate-функции (совместима с @/i18n t). */
type TranslateFn = (key: string, vars?: Record<string, string | number>) => string;

/**
 * Zod-схема формы события — фабрика с t: сообщение cross-field refine идёт через i18n
 * (U-30: endAt > startAt; U-16: без сырых enum/сообщений в UI).
 */
function createEventFormSchema(translate: TranslateFn) {
  return z
    .object({
      title: z.string().min(1, 'Название обязательно').max(200),
      description: z.string().max(2000).optional().or(z.literal('')),
      location: z.string().max(200).optional().or(z.literal('')),
      startAt: z.string().min(1, 'Дата начала обязательна'),
      endAt: z.string().min(1, 'Дата окончания обязательна'),
      // S5 (V13): «Весь день» — boolean с дефолтом false (BE allDay @NotNull).
      allDay: z.boolean().default(false),
      type: z.enum(['MEETING', 'CALL', 'TASK', 'REMINDER']),
      // B1: backend CalendarCreateRequest.departmentId — @NotNull; пусто → validation error.
      departmentId: z.string().min(1, translate('calendar.departmentRequired')),
    })
    // U-30: FE cross-field — не уходить на сервер с endAt <= startAt (BE тоже 400).
    .refine(
      (v) => {
        if (!v.startAt || !v.endAt) return true; // empty ловит min(1)
        const start = new Date(v.startAt).getTime();
        const end = new Date(v.endAt).getTime();
        return !Number.isNaN(start) && !Number.isNaN(end) && start < end;
      },
      // U-16 sweep: сообщение через i18n-ключ (ru: «Дата окончания должна быть позже даты начала»).
      { path: ['endAt'], message: translate('calendar.endAtAfterStart') },
    );
}

export type CalendarEventFormValues = z.infer<ReturnType<typeof createEventFormSchema>>;

interface CalendarEventFormProps {
  /** Значения по умолчанию. */
  defaultValues?: Partial<CalendarEventFormValues>;
  /** Колбэк submit. */
  onSubmit: SubmitHandler<CalendarEventFormValues>;
  /** Флаг процесса. */
  submitting?: boolean;
  /** Текст кнопки. */
  submitLabel?: string;
}

const TYPE_VALUES: CalendarType[] = ['MEETING', 'CALL', 'TASK', 'REMINDER'];

/** Форма создания/редактирования события календаря. */
export function CalendarEventForm({
  defaultValues,
  onSubmit,
  submitting = false,
  submitLabel = 'Сохранить',
}: CalendarEventFormProps): JSX.Element {
  const eventFormSchema = useMemo(() => createEventFormSchema(t), [t]);
  const { control, handleSubmit } = useForm<CalendarEventFormValues>({
    resolver: zodResolver(eventFormSchema),
    defaultValues: {
      title: '',
      description: '',
      location: '',
      startAt: '',
      endAt: '',
      allDay: false,
      type: 'MEETING',
      departmentId: '',
      ...defaultValues,
    },
  });

  // S5: allDay → границы выбранного дня (startOfDay/endOfDay), до onSubmit.
  const submitWithAllDayNormalization: SubmitHandler<CalendarEventFormValues> = (values) => {
    if (values.allDay) {
      const start = new Date(values.startAt);
      onSubmit({ ...values, startAt: startOfDay(start).toISOString(), endAt: endOfDay(start).toISOString() });
      return;
    }
    onSubmit(values);
  };

  return (
    <Box component="form" onSubmit={handleSubmit(submitWithAllDayNormalization)} noValidate>
      <Stack spacing={2}>
        <Controller
          name="title"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              label="Название"
              required
              fullWidth
              error={Boolean(fieldState.error)}
              helperText={fieldState.error?.message}
              inputProps={{ 'aria-label': 'Название события' }}
            />
          )}
        />
        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value ?? ''}
              label="Описание"
              multiline
              rows={2}
              fullWidth
              inputProps={{ 'aria-label': 'Описание события' }}
            />
          )}
        />
        <Controller
          name="location"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value ?? ''}
              label="Место проведения"
              fullWidth
              inputProps={{ 'aria-label': 'Место проведения' }}
            />
          )}
        />
        <Stack direction="row" spacing={2}>
          <DateTimePickerField
            name="startAt"
            control={control}
            label="Начало"
            required
            helperText="Дата и время начала"
            aria-label="Дата начала"
          />
          <DateTimePickerField
            name="endAt"
            control={control}
            label="Окончание"
            required
            helperText="Дата и время окончания"
            aria-label="Дата окончания"
          />
        </Stack>
        {/* S5 (V13): «Весь день» — payload allDay → startOfDay/endOfDay выбранного дня. */}
        <Controller
          name="allDay"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={
                <Switch
                  checked={field.value}
                  onChange={(event) => field.onChange(event.target.checked)}
                  inputProps={{ 'aria-label': 'Весь день' }}
                />
              }
              label="Весь день"
            />
          )}
        />
        <Controller
          name="type"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              select
              required
              fullWidth
              label="Тип"
              inputProps={{ 'aria-label': 'Тип события' }}
            >
              {TYPE_VALUES.map((value) => (
                <MenuItem key={value} value={value}>
                  {/* R41 / U-16: подпись типа через i18n (calendar.type.*), не hardcoded. */}
                  {formatEventType(value, t)}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
        {/* B1 (обязателен) + U-20: поисковый селект департамента вместо 1258 MenuItem. */}
        <Controller
          name="departmentId"
          control={control}
          render={({ field, fieldState }) => (
            <DepartmentAutocomplete
              label={t('calendar.department')}
              value={field.value}
              onChange={field.onChange}
              required
              error={Boolean(fieldState.error)}
              helperText={fieldState.error?.message ?? undefined}
              aria-label={t('calendar.department')}
            />
          )}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          {/* B1: submit всегда доступен — валидацию (включая департамент) показывает RHF. */}
          <Button type="submit" variant="contained" disabled={submitting}>
            {submitLabel}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}
